import { useState } from 'react';
import type { ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, CheckCircle2, Link2, Loader2, RefreshCw, Trash2, X, Youtube } from 'lucide-react';
import { toast } from 'sonner';

import AdminShell from '../components/AdminShell';
import PaginationControls from '../components/PaginationControls';
import { useAdminCatalog } from '../hooks/useAdminCatalog';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';

interface ConfirmAction {
  title: string;
  message: string;
  confirmLabel: string;
  tone?: 'danger' | 'default';
  action: () => Promise<void>;
}

const StatusPill = ({
  active,
  icon: Icon,
  label,
  detail,
}: {
  active: boolean;
  icon: ComponentType<{ className?: string }>;
  label: string;
  detail?: string | null;
}) => (
  <span
    className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] ${
      active
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
        : 'border-zinc-700 bg-zinc-950 text-zinc-500'
    }`}
    title={detail || label}
  >
    <Icon className="h-3 w-3 shrink-0" />
    <span className="truncate">{label}</span>
  </span>
);

export default function UsersPage() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [blockedFilter, setBlockedFilter] = useState<'all' | 'blocked'>('all');
  const { users, pagination, loading, refresh, selectedUser, loadingDetail, loadUserDetail, clearSelectedUser, blockUser, unblockUser, verifyUserEmail, deleteUser } = useAdminUsers(query, page, 10, blockedFilter === 'blocked' ? true : undefined);
  const { plans, assignSubscription, revokeSubscription, resetMonthlyQuota, mutating } = useAdminCatalog('viewer-lens', undefined, 1, 10, {
    loadPlans: true,
    loadSubscriptions: false,
    enableAutoRefresh: false,
  });
  const [selectedPlanId, setSelectedPlanId] = useState<number | ''>('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ id: number; email: string } | null>(null);
  const [cancelAtCycleEnd, setCancelAtCycleEnd] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [resetQuotaTarget, setResetQuotaTarget] = useState<{ userId: number; email: string; productCode: string; planName: string } | null>(null);
  const [resetQuotaTypes, setResetQuotaTypes] = useState<{ analyses: boolean; reanalyses: boolean }>({ analyses: false, reanalyses: false });
  const [resetQuotaLoading, setResetQuotaLoading] = useState(false);
  const [openingUserDetail, setOpeningUserDetail] = useState(false);

  useLockBodyScroll(Boolean(confirmAction) || Boolean(selectedUser) || Boolean(revokeTarget) || Boolean(resetQuotaTarget) || showDeleteModal);

  const handleDeleteUserSubmit = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      await deleteUser(selectedUser.user.id, deleteReason || undefined);
      toast.success(`Account for ${selectedUser.user.email} permanently deleted.`);
      setShowDeleteModal(false);
      setDeleteReason('');
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Delete failed.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeUserDetail = () => {
    clearSelectedUser();
    setSelectedPlanId('');
    setOpeningUserDetail(false);
  };

  const handleOpenUserDetail = async (userId: number) => {
    setOpeningUserDetail(true);
    try {
      await loadUserDetail(userId);
    } finally {
      setOpeningUserDetail(false);
    }
  };

  const handleAssign = () => {
    if (!selectedUser || !selectedPlanId) return;
    const plan = plans.find((p) => p.id === Number(selectedPlanId));
    setConfirmAction({
      title: 'Assign subscription?',
      message: `This will assign "${plan?.name || 'the selected plan'}" to ${selectedUser.user.email}. If they already have an active subscription this will fail — use Force Assign to override.`,
      confirmLabel: 'Assign',
      action: async () => {
        await assignSubscription({
          userId: selectedUser.user.id,
          planId: Number(selectedPlanId),
          status: 'active',
          provider: 'admin',
        });
        toast.success('Subscription assigned.');
        await refresh();
        await loadUserDetail(selectedUser.user.id);
        setSelectedPlanId('');
      },
    });
  };

  const handleForceAssign = () => {
    if (!selectedUser || !selectedPlanId) return;
    const plan = plans.find((p) => p.id === Number(selectedPlanId));
    setConfirmAction({
      title: '⚠️ Force assign subscription?',
      message: `This will immediately revoke any existing subscription for ${selectedUser.user.email} and assign "${plan?.name || 'the selected plan'}". This cannot be undone.`,
      confirmLabel: 'Force Assign',
      action: async () => {
        await assignSubscription({
          userId: selectedUser.user.id,
          planId: Number(selectedPlanId),
          status: 'active',
          provider: 'admin',
          force: true,
        });
        toast.success('Subscription force-assigned.');
        await refresh();
        await loadUserDetail(selectedUser.user.id);
        setSelectedPlanId('');
      },
    });
  };

  const handleRevoke = (subscriptionId: number, email: string) => {
    setRevokeTarget({ id: subscriptionId, email });
    setCancelAtCycleEnd(true); // default to cycle end
  };

  const executeRevoke = async () => {
    if (!revokeTarget) return;
    setConfirmLoading(true);
    try {
      await revokeSubscription(revokeTarget.id, cancelAtCycleEnd);
      toast.success('Subscription cancelled.');
      setRevokeTarget(null);
      await refresh();
      if (selectedUser) {
        await loadUserDetail(selectedUser.user.id);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Action failed.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    try {
      await confirmAction.action();
      setConfirmAction(null);
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Action failed.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleBlockUserSubmit = async () => {
    if (!selectedUser) return;
    setConfirmLoading(true);
    try {
      await blockUser(selectedUser.user.id, blockReason);
      toast.success('User blocked successfully.');
      setShowBlockModal(false);
      setBlockReason('');
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Action failed.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleUnblockUserSubmit = async () => {
    if (!selectedUser) return;
    if (!confirm(`Are you sure you want to unblock ${selectedUser.user.email}?`)) return;
    setConfirmLoading(true);
    try {
      await unblockUser(selectedUser.user.id);
      toast.success('User unblocked successfully.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Action failed.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleVerifyUserEmail = () => {
    if (!selectedUser) return;
    setConfirmAction({
      title: 'Verify email manually?',
      message: `This will mark ${selectedUser.user.email} as email verified and clear any pending OTP challenge. Use this only when you have confirmed the account owner another way.`,
      confirmLabel: 'Mark verified',
      action: async () => {
        await verifyUserEmail(selectedUser.user.id);
        toast.success('Email marked as verified.');
      },
    });
  };

  return (
    <AdminShell
      title="Users"
      description="Global user directory across Novera Labs products, with subscription and usage context."
      actions={(
        <>
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search users"
            className="h-9 px-3 rounded-lg border border-zinc-700 bg-zinc-900 text-zinc-100 text-xs"
          />
          <select
            value={blockedFilter}
            onChange={(event) => {
              setBlockedFilter(event.target.value as 'all' | 'blocked');
              setPage(1);
            }}
            className="h-9 px-3 rounded-lg border border-zinc-700 bg-zinc-900 text-zinc-100 text-xs"
          >
            <option value="all">All Users</option>
            <option value="blocked">Blocked Only</option>
          </select>
          <button
            onClick={() => refresh().catch(() => undefined)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
          >
            Refresh
          </button>
        </>
      )}
    >
      {loading && !users.length ? (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
        </div>
      ) : null}

      <div className="space-y-6">
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Plan</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Subscription</th>
                  <th className="py-2 pr-4">Analyses</th>
                  <th className="py-2 pr-4">Signals</th>
                  <th className="py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-zinc-800/70 align-top cursor-pointer hover:bg-zinc-950/50"
                    onClick={() => { handleOpenUserDetail(user.id).catch(() => undefined); }}
                  >
                    <td className="py-3 pr-4">
                      <p className="text-zinc-100">{user.name || 'Unnamed user'}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                      {(user.productCodes || []).length ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(user.productCodes || []).map((productCode) => (
                            <span key={productCode} className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-[11px] text-zinc-400">
                              {productCode}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {user.isAdmin ? <span className="inline-flex px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-400">Admin</span> : null}
                        {user.isBlocked ? <span className="inline-flex px-2 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-[11px] text-red-400">Blocked</span> : null}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-300">{user.plan}</td>
                    <td className="py-3 pr-4">
                      {user.emailVerified ? (
                        <div>
                          <span className="inline-flex px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-[11px] text-emerald-400">Verified</span>
                          <p className="mt-1 text-[11px] text-zinc-500">{user.emailVerifiedAt ? new Date(user.emailVerifiedAt).toLocaleString() : '—'}</p>
                        </div>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-400">Unverified</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-zinc-300">{user.subscriptionPlanName || '—'}</p>
                      <p className="text-xs text-zinc-500">{user.subscriptionProductCode || user.subscriptionStatus || 'No subscription'}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-zinc-300">{user.analysesCount}</p>
                      <p className="text-[11px] text-zinc-500">{user.channelAnalysesCount || 0} channel</p>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1.5">
                        <StatusPill
                          active={Boolean(user.youtubeConnected || user.hasConnectedChannels)}
                          icon={Youtube}
                          label={user.youtubeConnected ? 'YouTube linked' : 'No channel'}
                          detail={user.youtubeChannelTitle}
                        />
                        <StatusPill
                          active={Boolean(user.hasAnalyses || user.analysesCount > 0)}
                          icon={BarChart3}
                          label={(user.hasAnalyses || user.analysesCount > 0) ? 'Has analyses' : 'No analyses'}
                          detail={`${user.analysesCount} total analyses`}
                        />
                        {user.socialConnected ? (
                          <StatusPill
                            active
                            icon={Link2}
                            label="Social linked"
                            detail={user.instagramUsername}
                          />
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 text-zinc-500 text-xs">{new Date(user.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls pagination={pagination} onPageChange={setPage} />
        </section>
      </div>

      {(selectedUser || openingUserDetail) && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl relative">
            <button 
              onClick={closeUserDetail} 
              className="absolute top-5 right-5 inline-flex items-center justify-center h-8 w-8 rounded-lg border border-zinc-700 bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <h2 className="text-xl font-bold text-zinc-100 mb-6">User Detail</h2>

            {loadingDetail || openingUserDetail || !selectedUser ? (
              <div className="flex items-center gap-2 text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading user detail…
              </div>
            ) : null}

            {selectedUser ? (
              <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <p className="text-sm font-semibold text-zinc-100">{selectedUser.user.name || 'Unnamed user'}</p>
                <p className="text-xs text-zinc-500 mt-1">{selectedUser.user.email}</p>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2 py-1 rounded-full border border-zinc-700 bg-zinc-900 text-zinc-300">{selectedUser.user.plan}</span>
                  {selectedUser.user.emailVerified ? (
                    <span className="px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">Email verified</span>
                  ) : (
                    <span className="px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400">Email unverified</span>
                  )}
                  {selectedUser.user.isAdmin ? <span className="px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400">Admin</span> : null}
                  {selectedUser.user.isBlocked ? <span className="px-2 py-1 rounded-full border border-red-500/30 bg-red-500/10 text-red-400 font-semibold">Blocked</span> : null}
                </div>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-100">Account Status</h3>
                <div className="mt-3 space-y-4">
                  <div className={`rounded-lg border p-3 text-xs ${selectedUser.user.emailVerified ? 'border-emerald-900/40 bg-emerald-950/10' : 'border-amber-900/40 bg-amber-950/10'}`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className={`font-semibold ${selectedUser.user.emailVerified ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {selectedUser.user.emailVerified ? 'Email is verified' : 'Email is not verified'}
                        </p>
                        <p className="mt-1 text-zinc-500">
                          {selectedUser.user.emailVerified
                            ? `Verified at: ${selectedUser.user.emailVerifiedAt ? new Date(selectedUser.user.emailVerifiedAt).toLocaleString() : '—'}`
                            : 'Unverified users can only start one new video analysis until their email is verified.'}
                        </p>
                        <p className="mt-1 text-zinc-500">Terms accepted: {selectedUser.user.termsAcceptedAt ? new Date(selectedUser.user.termsAcceptedAt).toLocaleString() : '—'}</p>
                      </div>
                      {!selectedUser.user.emailVerified ? (
                        <button
                          type="button"
                          onClick={handleVerifyUserEmail}
                          disabled={confirmLoading}
                          className="h-8 px-3 rounded-lg border border-emerald-500/30 text-emerald-300 text-xs hover:text-emerald-200 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                        >
                          Mark email verified
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {selectedUser.user.isBlocked ? (
                    <div className="space-y-3">
                      <div className="rounded-lg border border-red-900/40 bg-red-950/20 p-3 text-xs">
                        <p className="font-semibold text-red-400">Account is blocked</p>
                        <p className="mt-1 text-zinc-400">Reason: <span className="text-zinc-300">{selectedUser.user.blockedReason}</span></p>
                        <p className="mt-1 text-zinc-500">Blocked at: {selectedUser.user.blockedAt ? new Date(selectedUser.user.blockedAt).toLocaleString() : '—'}</p>
                      </div>
                      <button 
                        onClick={handleUnblockUserSubmit} 
                        disabled={confirmLoading}
                        className="h-8 px-3 rounded-lg border border-zinc-700 text-zinc-300 text-xs hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
                      >
                        {confirmLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5 inline" /> : null}
                        Unblock User
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-zinc-500 mb-3">User has normal access to the application.</p>
                      <button 
                        onClick={() => setShowBlockModal(true)} 
                        disabled={selectedUser.user.isAdmin}
                        className="h-8 px-3 rounded-lg border border-red-500/30 text-red-400 text-xs hover:text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={selectedUser.user.isAdmin ? "Admins cannot be blocked" : ""}
                      >
                        Block User
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-red-900/30 bg-red-950/10 p-4">
                <h3 className="text-sm font-semibold text-red-400">Danger Zone</h3>
                <p className="mt-1 text-xs text-zinc-500">
                  Permanently deletes the account and all associated analyses. This cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={selectedUser.user.isAdmin}
                  title={selectedUser.user.isAdmin ? 'Admin accounts cannot be deleted' : ''}
                  className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-red-500/40 text-red-400 text-xs hover:text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Account
                </button>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-100">Creator Signals</h3>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className={`rounded-lg border p-3 ${selectedUser.user.hasConnectedChannels ? 'border-emerald-900/40 bg-emerald-950/10' : 'border-zinc-800 bg-zinc-900/60'}`}>
                    <div className="flex items-center gap-2">
                      <Youtube className={`h-4 w-4 ${selectedUser.user.hasConnectedChannels ? 'text-emerald-400' : 'text-zinc-600'}`} />
                      <p className="text-xs font-semibold text-zinc-200">
                        {selectedUser.user.hasConnectedChannels ? 'Channel connected' : 'No channel connected'}
                      </p>
                    </div>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {selectedUser.user.youtubeChannelTitle || selectedUser.user.channelConnections?.[0]?.title || 'YouTube channel not linked yet'}
                    </p>
                    {(selectedUser.user.channelConnections || []).length ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(selectedUser.user.channelConnections || []).map((connection, index) => (
                          <span key={`${connection.provider}-${connection.id || index}`} className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400">
                            {connection.provider}{connection.connectedAt ? ` · ${new Date(connection.connectedAt).toLocaleDateString()}` : ''}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className={`rounded-lg border p-3 ${(selectedUser.recentAnalyses || []).length ? 'border-sky-900/40 bg-sky-950/10' : 'border-zinc-800 bg-zinc-900/60'}`}>
                    <div className="flex items-center gap-2">
                      <BarChart3 className={`h-4 w-4 ${(selectedUser.recentAnalyses || []).length ? 'text-sky-400' : 'text-zinc-600'}`} />
                      <p className="text-xs font-semibold text-zinc-200">
                        {(selectedUser.recentAnalyses || []).length ? 'Analysis activity found' : 'No analysis activity'}
                      </p>
                    </div>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {(selectedUser.recentAnalyses || []).length
                        ? `${selectedUser.recentAnalyses.length} recent video analyses · ${selectedUser.user.channelAnalysesCount || 0} channel runs`
                        : 'User has not run a video analysis yet.'}
                    </p>
                  </div>
                  <div className={`rounded-lg border p-3 sm:col-span-2 ${selectedUser.user.socialConnected ? 'border-violet-900/40 bg-violet-950/10' : 'border-zinc-800 bg-zinc-900/60'}`}>
                    <div className="flex items-center gap-2">
                      <Link2 className={`h-4 w-4 ${selectedUser.user.socialConnected ? 'text-violet-300' : 'text-zinc-600'}`} />
                      <p className="text-xs font-semibold text-zinc-200">
                        {selectedUser.user.socialConnected ? 'Social account linked' : 'No social account linked'}
                      </p>
                    </div>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {selectedUser.user.instagramUsername ? `Instagram: @${selectedUser.user.instagramUsername}` : 'Facebook/Instagram session not connected.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-100">Current Subscription</h3>
                {selectedUser.user.currentSubscription ? (
                  <div className="mt-3 space-y-1 text-xs text-zinc-400">
                    <p>Status: <span className="text-zinc-200">
                      {selectedUser.user.currentSubscription.status}
                      {selectedUser.user.currentSubscription.cancelAtCycleEnd && selectedUser.user.currentSubscription.status === 'active' ? <span className="text-amber-500/80 ml-1">(Cancels at period end)</span> : null}
                    </span></p>
                    <p>Product: <span className="text-zinc-200">{selectedUser.user.currentSubscription.product?.name || '—'}</span></p>
                    <p>Plan: <span className="text-zinc-200">{selectedUser.user.currentSubscription.plan?.name || '—'}</span></p>
                    <p>Current End: <span className="text-zinc-200">{selectedUser.user.currentSubscription.currentEnd ? new Date(selectedUser.user.currentSubscription.currentEnd).toLocaleString() : '—'}</span></p>
                    <p>Provider ID: <span className="text-zinc-200">{selectedUser.user.currentSubscription.providerSubscriptionId || '—'}</span></p>
                  </div>
                ) : <p className="mt-3 text-xs text-zinc-500">No active linked subscription.</p>}
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-100">Subscription Actions</h3>
                {plans.length ? (
                  <>
                    <p className="mt-2 text-[11px] text-zinc-500">{plans.length} plans available for manual assignment.</p>
                    <div className="mt-3 flex gap-2">
                      <select
                        value={selectedPlanId}
                        onChange={(event) => setSelectedPlanId(event.target.value ? Number(event.target.value) : '')}
                        className="flex-1 h-10 px-3 rounded-xl bg-zinc-900 border border-zinc-700 text-sm text-zinc-100"
                      >
                        <option value="">Select plan</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.product?.name || 'Product'} · {plan.name}</option>
                        ))}
                      </select>
                      <button onClick={handleAssign} disabled={!selectedPlanId || mutating} className="h-10 px-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold disabled:opacity-50">
                        {mutating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Assign'}
                      </button>
                      <button onClick={handleForceAssign} disabled={!selectedPlanId || mutating} title="Revokes any existing subscription and force-assigns the selected plan" className="h-10 px-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50">
                        {mutating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Force'}
                      </button>
                    </div>
                    <p className="mt-1.5 text-[10px] text-zinc-600">Force revokes any existing plan first — use when normal assign is blocked.</p>
                  </>
                ) : (
                  <p className="mt-3 text-xs text-zinc-500">No plans are available to assign yet. Create one in the Subscriptions module first.</p>
                )}
                {(selectedUser.subscriptions || []).length ? (
                  <div className="mt-4 space-y-2">
                    {(selectedUser.subscriptions || []).map((subscription) => {
                      const productCode = subscription.product?.code;
                      return (
                        <div key={subscription.id} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs text-zinc-200 truncate">{subscription.product?.name || 'Product'} · {subscription.plan?.name || 'Plan'}</p>
                            <p className="mt-1 text-[11px] text-zinc-500 truncate">
                              {subscription.status}
                              {subscription.cancelAtCycleEnd && subscription.status === 'active' ? <span className="text-amber-500/80"> (Cancels at period end)</span> : null}
                              {' · '}
                              {subscription.providerSubscriptionId || subscription.createdAt}
                            </p>
                          </div>
                          <div className="shrink-0 flex items-center gap-2">
                            {productCode ? (
                              <button
                                onClick={() => {
                                  setResetQuotaTypes({ analyses: false, reanalyses: false });
                                  setResetQuotaTarget({
                                    userId: selectedUser.user.id,
                                    email: selectedUser.user.email,
                                    productCode,
                                    planName: subscription.plan?.name || subscription.product?.name || 'this plan',
                                  });
                                }}
                                disabled={mutating}
                                className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 text-zinc-300 text-[11px] hover:text-zinc-100 hover:border-zinc-500 disabled:opacity-50 transition-colors"
                              >
                                <RefreshCw className="w-3 h-3" /> Reset quota
                              </button>
                            ) : null}
                            {subscription.status !== 'revoked' ? (
                              <button onClick={() => handleRevoke(subscription.id, selectedUser!.user.email)} disabled={mutating} className="h-8 px-2.5 rounded-lg border border-red-500/30 text-red-300 text-[11px] hover:text-red-200 disabled:opacity-50 transition-colors">
                                Revoke
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {/* No linked subscriptions — still allow quota reset if a product is known */}
                {!(selectedUser.subscriptions || []).length && plans[0]?.product?.code ? (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        setResetQuotaTypes({ analyses: false, reanalyses: false });
                        setResetQuotaTarget({
                          userId: selectedUser.user.id,
                          email: selectedUser.user.email,
                          productCode: plans[0].product!.code,
                          planName: 'Free tier',
                        });
                      }}
                      disabled={mutating}
                      className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 text-zinc-300 text-xs hover:text-zinc-100 hover:border-zinc-500 disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reset quota
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <h3 className="text-sm font-semibold text-zinc-100">Recent Analyses</h3>
                <div className="mt-3 space-y-3">
                  {(selectedUser.recentAnalyses || []).length ? (selectedUser.recentAnalyses || []).map((analysis) => (
                    <div key={analysis.sessionId} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 flex justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-zinc-200 truncate">{analysis.title || analysis.url}</p>
                        <p className="mt-1 text-[11px] text-zinc-500 truncate">{analysis.platform || 'unknown'} · {analysis.status}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-[11px] text-zinc-400">{analysis.commentsCount} comments</p>
                      </div>
                    </div>
                  )) : <p className="text-xs text-zinc-500">No recent analyses.</p>}
                </div>
              </div>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
      {revokeTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-zinc-100">Cancel Subscription?</h3>
            <p className="mt-2 text-sm text-zinc-400">
              Important: This action cannot be undone. The subscription for <span className="text-zinc-200 font-semibold">{revokeTarget.email}</span> will move to a cancelled state.
            </p>
            
            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 hover:bg-zinc-800/50 transition-colors">
                <input 
                  type="radio" 
                  name="cancelType" 
                  className="mt-1" 
                  checked={cancelAtCycleEnd === true} 
                  onChange={() => setCancelAtCycleEnd(true)} 
                />
                <div>
                  <p className="text-sm font-semibold text-zinc-200">Cancel at end of current billing cycle</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Next payment will not be charged. User retains access until the cycle ends.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-red-900/20 bg-red-950/10 hover:bg-red-900/20 transition-colors">
                <input 
                  type="radio" 
                  name="cancelType" 
                  className="mt-1" 
                  checked={cancelAtCycleEnd === false} 
                  onChange={() => setCancelAtCycleEnd(false)} 
                />
                <div>
                  <p className="text-sm font-semibold text-red-400">Cancel Immediately</p>
                  <p className="text-xs text-red-500/70 mt-0.5">Revokes access instantly. No prorated refund will be issued.</p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                disabled={confirmLoading}
                className="h-10 px-4 flex items-center justify-center rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                No, don't
              </button>
              <button
                type="button"
                onClick={executeRevoke}
                disabled={confirmLoading}
                className="h-10 px-6 flex items-center justify-center rounded-xl font-semibold bg-blue-600 hover:bg-blue-500 text-white transition-colors disabled:opacity-50 gap-2"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {confirmAction ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-zinc-100">{confirmAction.title}</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{confirmAction.message}</p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={confirmLoading}
                className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirmAction}
                disabled={confirmLoading}
                className={`h-10 px-4 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center gap-2 ${
                  confirmAction.tone === 'danger'
                    ? 'bg-red-500 hover:bg-red-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-black'
                }`}
              >
                {confirmLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {confirmAction.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      
      {showBlockModal && selectedUser ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-zinc-100">Block User</h3>
            <p className="mt-2 text-sm text-zinc-400">
              This will immediately revoke access to the application for <span className="text-zinc-200 font-semibold">{selectedUser.user.email}</span>.
            </p>
            
            <div className="mt-5">
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Reason (optional)</label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="e.g. Violation of TOS, Spam, Fraud..."
                className="w-full h-24 p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => {
                  setShowBlockModal(false);
                  setBlockReason('');
                }}
                disabled={confirmLoading}
                className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBlockUserSubmit}
                disabled={confirmLoading}
                className="h-10 px-6 flex items-center justify-center rounded-xl font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 gap-2"
              >
                {confirmLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Confirm Block
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showDeleteModal && selectedUser ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-zinc-100">Delete Account</h3>
            <p className="mt-2 text-sm text-zinc-400">
              This will permanently delete <span className="font-semibold text-zinc-200">{selectedUser.user.email}</span> and all their analyses. This action cannot be undone.
            </p>

            <div className="mt-5">
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">
                Reason <span className="text-zinc-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g. User requested via support ticket, TOS violation…"
                className="w-full h-24 p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 resize-none"
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => { setShowDeleteModal(false); setDeleteReason(''); }}
                disabled={deleteLoading}
                className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteUserSubmit}
                disabled={deleteLoading}
                className="h-10 px-6 flex items-center justify-center rounded-xl font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 gap-2"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {resetQuotaTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-zinc-100">Reset Quota</h3>
            <p className="mt-2 text-sm text-zinc-400">
              Select which quotas to reset for <span className="text-zinc-200 font-semibold">{resetQuotaTarget.email}</span> on <span className="text-zinc-200 font-semibold">{resetQuotaTarget.planName}</span>. This resets their recorded usage for the current month.
            </p>

            <div className="mt-5 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 hover:bg-zinc-800/50 transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={resetQuotaTypes.analyses}
                  onChange={(e) => setResetQuotaTypes((prev) => ({ ...prev, analyses: e.target.checked }))}
                />
                <div>
                  <p className="text-sm font-semibold text-zinc-200">Monthly analyses quota</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Resets the number of analysis runs used this month.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 bg-zinc-950/50 hover:bg-zinc-800/50 transition-colors">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={resetQuotaTypes.reanalyses}
                  onChange={(e) => setResetQuotaTypes((prev) => ({ ...prev, reanalyses: e.target.checked }))}
                />
                <div>
                  <p className="text-sm font-semibold text-zinc-200">Re-analyses quota</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Resets the number of re-analysis runs used this month.</p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setResetQuotaTarget(null)}
                disabled={resetQuotaLoading}
                className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm hover:text-white hover:bg-zinc-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resetQuotaLoading || (!resetQuotaTypes.analyses && !resetQuotaTypes.reanalyses)}
                onClick={async () => {
                  if (!resetQuotaTarget) return;
                  const type = resetQuotaTypes.analyses && resetQuotaTypes.reanalyses
                    ? 'both'
                    : resetQuotaTypes.analyses
                      ? 'analyses'
                      : 'reanalyses';
                  setResetQuotaLoading(true);
                  try {
                    await resetMonthlyQuota(resetQuotaTarget.userId, resetQuotaTarget.productCode, type);
                    // Refresh users list and re-fetch detail so updated quota is visible immediately
                    await refresh();
                    if (selectedUser?.user.id === resetQuotaTarget.userId) {
                      await loadUserDetail(resetQuotaTarget.userId);
                    }
                    toast.success('Quota reset successfully.');
                    setResetQuotaTarget(null);
                  } catch {
                    toast.error('Failed to reset quota.');
                  } finally {
                    setResetQuotaLoading(false);
                  }
                }}
                className="h-10 px-6 flex items-center justify-center rounded-xl font-semibold bg-amber-500 hover:bg-amber-600 text-black transition-colors disabled:opacity-50 gap-2"
              >
                {resetQuotaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Reset selected
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
